import { TEAMS } from 'shared/rollbar/global_prefixes';
import { logError } from 'client/javascript/util/logger';
import { toggleLockScroll } from 'client/javascript/lock_scroll';
import { div } from 'client/javascript/util/dom_utils';
import { isMobileBreakpoint } from 'client/javascript/util/device_breakpoints';
import { setupLocationFilter, FILTER_NAME as LOCATION_FILTER_NAME } from './search_widget/location_filter';
import {
  setupWorkspaceTypeFilter,
  FILTER_NAME as WORKSPACE_TYPE_FILTER_NAME
} from './search_widget/workspace_type_filter';
import { setupCapacityFilter, FILTER_NAME as CAPACITY_FILTER_NAME } from './search_widget/capacity_filter';

export const LOGGING_CONFIG = {
  prefix: '[HeroContainerSearchWidgetUI]',
  team: TEAMS.MARKETING
};

export const CLASSES = {
  MODULE_ACTIVE: 'search-widget__container--active',
  MODULE_FILTER_ITEM_ACTIVE: 'search-widget-filters__item--active',
  MODULE_DROPDOWN_ITEM_ACTIVE: 'search-widget-dropdown__item--active',
  MODULE_BACKDROP: 'search-widget__backdrop',
  MODULE_BACKDROP_HIDE: 'search-widget__backdrop--hide',
  MODULE_FILTER_ITEM: 'search-widget-filter-item-js'
};

export const SELECTORS = {
  MODULE: '.search-widget__container',
  MODULE_WRAPPER: '.search-widget__wrapper',
  MODULE_FILTERS: '.search-widget-filters__wrapper',
  MODULE_FILTERS_ITEM: '.search-widget-filters__item',
  MODULE_PLACEHOLDER: '.search-widget__placeholder',
  MODULE_SEARCH_BTN: '.search-widget__search-btn',
  MODULE_FILTERS_ITEM_DEFAULT_VALUE: '.search-widget-filters__item-default-value',
  MODULE_DROPDOWN: '.search-widget__dropdown',
  MODULE_DROPDOWN_ACCORDION_INPUT: '.search-widget-dropdown__accordion-input',
  MODULE_DROPDOWN_ITEM: '.search-widget-dropdown__item',
  MODULE_FILTER_ITEM: `.${CLASSES.MODULE_FILTER_ITEM}`
};

const FILTERS = {
  [LOCATION_FILTER_NAME]: setupLocationFilter,
  [WORKSPACE_TYPE_FILTER_NAME]: setupWorkspaceTypeFilter,
  [CAPACITY_FILTER_NAME]: setupCapacityFilter
};

const KEYBOARD_KEY_NAMES = {
  TAB: 'Tab',
  ENTER: 'Enter',
  ESCAPE: 'Escape'
};
const INDENT_TOP = 24;

class SearchWidgetModule {
  activeFilter = '';

  focusArea = {
    firstElement: null,
    lastElement: null
  };

  constructor() {
    this.moduleContainerDomNode = document.querySelector(SELECTORS.MODULE);

    if (!this.moduleContainerDomNode) {
      return;
    }

    this.init();
  }

  static create() {
    return new SearchWidgetModule();
  }

  init() {
    this.moduleWrapperDomNode = this.moduleContainerDomNode.querySelector(SELECTORS.MODULE_WRAPPER);
    this.searchBtnDomNode = this.moduleContainerDomNode.querySelector(SELECTORS.MODULE_SEARCH_BTN);
    this.placeholderDomNode = this.moduleContainerDomNode.querySelector(SELECTORS.MODULE_PLACEHOLDER);
    this.dropdownDomNode = this.moduleContainerDomNode.querySelector(SELECTORS.MODULE_DROPDOWN);
    this.desktopFiltersDomNodes = this.moduleContainerDomNode.querySelectorAll(
      `${SELECTORS.MODULE_FILTERS} ${SELECTORS.MODULE_FILTERS_ITEM}`
    );
    this.mobileFiltersDomNodes = this.dropdownDomNode.querySelectorAll(SELECTORS.MODULE_FILTERS_ITEM);

    this.moduleContainerDomNode.addEventListener('click', this.handleCloseOutsideWrapper);

    this.initBackdrop();
    this.initPlaceholder();
  }

  setupFilters() {
    try {
      if (this.moduleContainerDomNode) {
        this.initFilters();
      }
    } catch (error) {
      logError(`${LOGGING_CONFIG.prefix}[setupFilters()] failed to setup search widget`, error, {
        team: LOGGING_CONFIG.team
      });
    }
  }

  initBackdrop = () => {
    // Create an instance of DOM node and using reference of the backdropDomNode to append to DOM or remove from DOM.
    this.backdropDomNode = div({
      className: CLASSES.MODULE_BACKDROP,
      events: {
        click: this.close,
        transitionend: ({ target }) => {
          // After the end of the hiding animation removing class of the animation because we use the same instance of DOM node
          // and its state keeps after the next appending to DOM.
          target.classList.remove(CLASSES.MODULE_BACKDROP_HIDE);

          // Remove only its reference from DOM without deleting its instance and keeping its state without changes.
          target.remove();
        }
      }
    });
  };

  initPlaceholder() {
    this.placeholderDomNode.addEventListener('click', this.togglePlaceholder);
  }

  handleEscapeClose = () => {
    this.close();
    document.activeElement.blur();
  };

  handleCloseOutsideWrapper = ({ target }) => {
    if (target.classList.contains(CLASSES.MODULE_ACTIVE)) {
      this.close();
    }
  };

  close = () => {
    toggleLockScroll();
    this.backdropDomNode.classList.add(CLASSES.MODULE_BACKDROP_HIDE);
    document.removeEventListener('keydown', this.handleKeydown);

    this.activeFilter = '';
    this.moduleContainerDomNode.style.height = '';
    this.moduleContainerDomNode.classList.remove(CLASSES.MODULE_ACTIVE);
    if (this.activeFilterDomNode) {
      this.activeFilterDomNode.classList.remove(CLASSES.MODULE_FILTER_ITEM_ACTIVE);
    }
  };

  open = () => {
    const { top } = this.moduleWrapperDomNode.getBoundingClientRect();

    toggleLockScroll();
    if (isMobileBreakpoint()) {
      this.backdropDomNode.style.height = `${window.innerHeight - top - INDENT_TOP}px`;
    }
    document.body.appendChild(this.backdropDomNode);
    document.addEventListener('keydown', this.handleKeydown);

    this.moduleContainerDomNode.classList.add(CLASSES.MODULE_ACTIVE);
    this.moduleContainerDomNode.style.height = `${window.innerHeight - top}px`;
  };

  initFilters = () => {
    this.initDesktopFilters();
    this.initMobileFilters();
  };

  initDesktopFilters = () => {
    this.desktopFiltersDomNodes.forEach((domNode) => {
      const { filterName } = domNode.dataset;

      domNode.addEventListener('click', this.handleDesktopFilter);
      domNode.addEventListener('keypress', (event) => {
        if (event.key === KEYBOARD_KEY_NAMES.ENTER) {
          this.handleDesktopFilter(event);
        }
      });

      this.callCorrespondingFilterHandler(filterName);
    });
  };

  initMobileFilters = () => {
    this.mobileFiltersDomNodes.forEach((domNode) => {
      domNode.addEventListener('click', this.handleMobileFilter);
    });
  };

  callCorrespondingFilterHandler(filterName) {
    if (typeof FILTERS[filterName] === 'function') {
      FILTERS[filterName](this.moduleContainerDomNode);
    }
  }

  setFilterValue = (filterName, value) => {
    this.moduleContainerDomNode
      .querySelectorAll(`[data-filter-name="${filterName}"] ${SELECTORS.MODULE_FILTERS_ITEM_DEFAULT_VALUE}`)
      .forEach((filterValueDomNode) => {
        // eslint-disable-next-line no-param-reassign
        filterValueDomNode.textContent = value;
      });
  };

  updateSearchUrl = (name = '', value = '') => {
    const url = new URL(this.searchBtnDomNode.href);
    url.searchParams.set(name, value);
    this.searchBtnDomNode.href = url.href;
    return url.href;
  };

  togglePlaceholder = () => {
    this.setDefaultMobileFilter();

    if (this.moduleContainerDomNode.classList.contains(CLASSES.MODULE_ACTIVE)) {
      this.close();
    } else {
      this.open();
    }
  };

  setDefaultMobileFilter = () => {
    const [firstMobileFilterDomNode] = this.mobileFiltersDomNodes;
    if (!firstMobileFilterDomNode) {
      return;
    }

    const { filterName, filterIndex } = firstMobileFilterDomNode.dataset;

    this.activeFilter = filterName;
    this.setDropdownItemState(filterIndex);
  };

  setDropdownItemState = (dropdownIndex = 0, isChecked = true) => {
    const dropdownAccordionInputDomNode = this.dropdownDomNode.querySelector(
      `${SELECTORS.MODULE_DROPDOWN_ACCORDION_INPUT}[data-dropdown-index="${dropdownIndex}"]`
    );

    if (dropdownAccordionInputDomNode) {
      dropdownAccordionInputDomNode.checked = isChecked;
    }
  };

  handleDesktopFilter = ({ currentTarget }) => {
    const { filterName, filterIndex } = currentTarget.dataset;

    if (this.activeFilter === filterName) {
      this.close();
      this.activeFilterDomNode.classList.remove(CLASSES.MODULE_FILTER_ITEM_ACTIVE);
      this.activeFilterDomNode = null;
      return;
    }

    if (!this.activeFilter) {
      this.open();
    }

    if (this.activeFilterDomNode) {
      this.activeFilterDomNode.classList.remove(CLASSES.MODULE_FILTER_ITEM_ACTIVE);
    }

    this.activeFilterDomNode = currentTarget;
    this.activeFilter = filterName;
    this.activeFilterDomNode.classList.add(CLASSES.MODULE_FILTER_ITEM_ACTIVE);

    this.setDropdownItemState(filterIndex);
    this.trapFocus();
  };

  handleMobileFilter = ({ currentTarget }) => {
    const { filterName, filterIndex } = currentTarget.dataset;

    if (this.activeFilter === filterName) {
      this.activeFilter = '';
      this.setDropdownItemState(filterIndex, false);
    } else {
      this.activeFilter = filterName;
      this.setDropdownItemState(filterIndex);
    }
  };

  trapFocus = () => {
    const mapFocusableElements = this.getFocusableElements();
    const firstFocusableElement = mapFocusableElements[0];
    const lastFocusableElement = mapFocusableElements.pop();

    this.updateFocusArea('firstElement', firstFocusableElement);
    this.updateFocusArea('lastElement', lastFocusableElement);
  };

  handleKeydown = (event) => {
    if (event.key === KEYBOARD_KEY_NAMES.ESCAPE) {
      this.handleEscapeClose();
    } else if (event.key === KEYBOARD_KEY_NAMES.TAB) {
      this.handleFocusableItems(event);
    }
  };

  handleFocusableItems = (event) => {
    const isFocusOutside = !document.activeElement.closest(SELECTORS.MODULE);

    if (event.shiftKey) {
      if (document.activeElement === this.focusArea.firstElement || isFocusOutside) {
        this.focusArea.lastElement.focus();
        event.preventDefault();
      }
    } else if (document.activeElement === this.focusArea.lastElement || isFocusOutside) {
      this.focusArea.firstElement.focus();
      event.preventDefault();
    }
  };

  updateFocusArea = (type, value) => {
    this.focusArea[type] = value;
  };

  getFocusableElements = () => {
    return Array.from(
      this.moduleContainerDomNode.querySelectorAll(
        `${SELECTORS.MODULE_FILTER_ITEM}, ${SELECTORS.MODULE_SEARCH_BTN}, [data-dropdown-name="${this.activeFilter}"] [tabindex="0"]`
      )
    ).filter((domNode) => {
      const { visibility } = window.getComputedStyle(domNode);
      return visibility === 'visible';
    });
  };
}

// Export and use only one instance of SearchWidgetModule in all filters and have only one state of the module.
// And to control initialization of SearchWidgetModule in tests to prevent run constructor method immediate during import call.
let searchWidgetInstance;
export const getSearchWidgetInstance = () => {
  if (!searchWidgetInstance) {
    searchWidgetInstance = SearchWidgetModule.create();
  }
  return searchWidgetInstance;
};
